// The embed session's DPoP signing key, kept in IndexedDB as a non-extractable CryptoKey: it
// survives reloads, but the private half can never be exported (sessionStorage would require that).
import { createStore, del, get, set } from 'idb-keyval'
import { DPOP_KEY_ALGORITHM } from './embed-dpop-proof'
import { embedInstanceId } from './embed-session'

// A dedicated store: console logout clears idb-keyval's default store for the whole origin,
// which would destroy every embed key. Created lazily (no IndexedDB in tests or blocked storage).
let storeHandle: ReturnType<typeof createStore> | undefined
const defaultStore = () => {
  if (!storeHandle) {
    storeHandle = createStore('embed-dpop', 'keys')
  }
  return storeHandle
}

// Injectable store for tests (no IndexedDB under happy-dom).
export interface DpopKeyStore {
  read(key: string): Promise<CryptoKeyPair | undefined>
  write(key: string, value: CryptoKeyPair): Promise<void>
  remove(key: string): Promise<void>
}

const idbStore: DpopKeyStore = {
  read: (key) => get<CryptoKeyPair>(key, defaultStore()),
  write: (key, value) => set(key, value, defaultStore()),
  remove: (key) => del(key, defaultStore()),
}

// Namespaced per embed instance like the session, so one iframe can never sign for another.
const keyName = (): string => {
  const instance = embedInstanceId()
  // Fails closed: no instance id, no key name (never a shared one).
  return instance ? `dpop-key:${instance}` : ''
}

// Returns undefined without IndexedDB; callers treat that as 'no proof can be produced'.
export const getOrCreateDpopKeyPair = async (
  store: DpopKeyStore = idbStore,
): Promise<CryptoKeyPair | undefined> => {
  const name = keyName()
  if (!name) {
    return undefined
  }
  // Single-flight: concurrent cold-boot callers must get the same key, or the session could
  // be bound to a key that was never stored.
  const flight = inFlight.get(name)
  if (flight) {
    return flight
  }
  const started = (async () => {
    try {
      const existing = await store.read(name)
      if (existing?.privateKey && existing?.publicKey) {
        return existing
      }
      // Non-extractable, sign/verify only.
      const keyPair = (await crypto.subtle.generateKey(DPOP_KEY_ALGORITHM, false, [
        'sign',
        'verify',
      ])) as CryptoKeyPair
      await store.write(name, keyPair)
      // First writer wins: return what is stored, since another frame may have written in between.
      const stored = await store.read(name)
      return stored?.privateKey && stored?.publicKey ? stored : keyPair
    } catch {
      return undefined
    } finally {
      // Cleared on settle so a failed generation can be retried.
      inFlight.delete(name)
    }
  })()
  inFlight.set(name, started)
  return started
}

const inFlight = new Map<string, Promise<CryptoKeyPair | undefined>>()

export const clearDpopKeyPair = async (store: DpopKeyStore = idbStore): Promise<void> => {
  try {
    const name = keyName()
    if (name) {
      await store.remove(name)
    }
  } catch {
    // Best-effort: a key without a session is inert.
  }
}
